import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
import { deleteMotivoOuvidoria, getMotivosOuvidoria } from '@spark/services';

const filtrosIniciais = {
  id: '',
  nomeMotivo: '',
  descricao: '',
  dataCriacaoInicial: '',
  dataCriacaoFinal: '',
};

const formatDateTime = value => {
  if (!value) return '';

  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const useMotivosOuvidoriaListagem = () => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [registroParaExcluir, setRegistroParaExcluir] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(20);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [filtrosUI, setFiltrosUI] = useState(filtrosIniciais);
  const hasLoadedOnce = useRef(false);

  const fetchData = useCallback(async () => {
    const isInitialLoad = !hasLoadedOnce.current;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setFilterLoading(true);
      }

      const response = await getMotivosOuvidoria({
        ...filtrosUI,
        pagina: paginaAtual,
        limitePorPagina: quantidadePorPagina,
      });

      const itens = Array.isArray(response?.itens) ? response.itens : [];
      const normalizados = itens.map(item => ({
        id: item.id,
        nomeMotivo: item.nomeMotivo || '',
        descricao: item.descricao || '',
        dataCriacao: item.dataCriacao || '',
        dataCriacaoFormatada: formatDateTime(item.dataCriacao),
      }));

      setRegistros(normalizados);
      setTotalRegistros(Number(response?.paginacao?.totalItens || 0));
    } catch (error) {
      console.error('Erro ao buscar motivos da ouvidoria:', error);
      setRegistros([]);
      setTotalRegistros(0);
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
      setFilterLoading(false);
    }
  }, [filtrosUI, paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterApply = useCallback(newFilters => {
    setPaginaAtual(1);
    setFiltrosUI({
      id: newFilters.id || '',
      nomeMotivo: newFilters.nomeMotivo || '',
      descricao: newFilters.descricao || '',
      dataCriacaoInicial: newFilters.dataCriacaoInicial || '',
      dataCriacaoFinal: newFilters.dataCriacaoFinal || '',
    });
  }, []);

  const handleLimparFiltros = useCallback(() => {
    setPaginaAtual(1);
    setFiltrosUI(filtrosIniciais);
  }, []);

  const handleExcluir = useCallback(id => {
    setRegistroParaExcluir(id);
    setShowDeleteModal(true);
  }, []);

  const handleCancelarExclusao = useCallback(() => {
    if (deleteLoading) {
      return;
    }

    setShowDeleteModal(false);
    setRegistroParaExcluir(null);
  }, [deleteLoading]);

  const handleConfirmarExclusao = useCallback(async () => {
    if (!registroParaExcluir) {
      return;
    }

    try {
      setDeleteLoading(true);
      await deleteMotivoOuvidoria(registroParaExcluir);
      notification.success('Motivo da ouvidoria excluido com sucesso!');
      setShowDeleteModal(false);
      setRegistroParaExcluir(null);
      await fetchData();
    } catch (error) {
      console.error('Erro ao excluir motivo da ouvidoria:', error);
    } finally {
      setDeleteLoading(false);
    }
  }, [fetchData, registroParaExcluir]);

  return {
    data: registros,
    loading,
    filterLoading,
    deleteLoading,
    showDeleteModal,
    totalRegistros,
    paginaAtual,
    quantidadePorPagina,
    filtrosUI,
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    handleCancelarExclusao,
    handleConfirmarExclusao,
    setPaginaAtual,
    setQuantidadePorPagina,
    refetch: fetchData,
  };
};
